/**
 * Baseline Accounting's own projects. Each starts from the baseline template; nothing is
 * shared with IAS accounting's models, which carry the IAS fields this discipline leaves
 * out. Laid out on the shared ProjectsPage, as every discipline's list is.
 */

import { useEffect, useRef, useState } from 'react';
import { MAX_FORECAST_YEARS, useMAccountingStore, type MLayout } from '../../../store/m-accounting';
import PdfImportProgress from '../PdfImportProgress';
import CompaniesHouseImport from '../CompaniesHouseImport';
import type { Discipline } from '../disciplines';
import { Button } from '../ui/Button';
import { Notice, Panel, ProjectRow, ProjectsPage } from '../ui/ProjectsPage';
import { FolderedProjects } from '../ui/ProjectFolders';
import { openSampleAt, useSampleCatalogue } from '../../../store/samples';
import { ReadAfresh } from '../ui/ReadAfresh';

export function MProjectList({
  discipline,
  onLeaveDiscipline,
}: {
  discipline: Discipline;
  onLeaveDiscipline: () => void;
}) {
  const list = useMAccountingStore((s) => s.list);
  const isLoading = useMAccountingStore((s) => s.isLoading);
  const error = useMAccountingStore((s) => s.error);
  const dismissError = useMAccountingStore((s) => s.dismissError);
  const loadList = useMAccountingStore((s) => s.loadList);
  const create = useMAccountingStore((s) => s.create);
  const open = useMAccountingStore((s) => s.open);
  const samples = useSampleCatalogue('/api/m');
  const remove = useMAccountingStore((s) => s.remove);
  const importProgress = useMAccountingStore((s) => s.importProgress);
  const importKind = useMAccountingStore((s) => s.importKind);
  const importWorkbook = useMAccountingStore((s) => s.importWorkbook);
  const importPdf = useMAccountingStore((s) => s.importPdf);
  const importFromCompaniesHouse = useMAccountingStore((s) => s.importFromCompaniesHouse);
  const downloadTemplate = useMAccountingStore((s) => s.downloadTemplate);
  const workbookInput = useRef<HTMLInputElement>(null);
  const pdfInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [reread, setReread] = useState(false);
  // The model's shape. The template's own - three filed years, two forecast, annual - keeps its
  // example company's figures; any other shape starts blank.
  const [shaped, setShaped] = useState(false);
  const [periodType, setPeriodType] = useState<MLayout['periodType']>('annual');
  const [firstYear, setFirstYear] = useState(new Date().getFullYear() - 3);
  const [filedYears, setFiledYears] = useState(3);
  const [forecastYears, setForecastYears] = useState(2);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const busy = importProgress !== null;

  return (
    <ProjectsPage
      discipline={discipline}
      onLeaveDiscipline={onLeaveDiscipline}
      intro="The baseline model, tab for tab: start from the template, or import a company's accounts and see where they land."
    >
      {error && <Notice onDismiss={dismissError}>{error}</Notice>}

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <Panel
          title="Start from the baseline template"
          description="Opens with the template's figures. Enter your own in the input cells of any tab."
        >
          <form
            className="flex gap-2"
            onSubmit={async (event) => {
              event.preventDefault();
              if (!name.trim()) return;
              const id = await create(
                name.trim(),
                shaped ? { periodType, firstYear, filedYears, forecastYears } : undefined,
              );
              if (id) setName('');
            }}
          >
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Company name"
              aria-label="Company name"
              className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-100"
            />
            <Button type="submit" variant="primary" disabled={!name.trim()}>
              Create
            </Button>
          </form>
          <label className="mt-3 flex items-center gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={shaped} onChange={(e) => setShaped(e.target.checked)} />
            Choose the years and periods (starts blank)
          </label>
          {shaped && (
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
              <label className="block">
                <span className="text-gray-600">Periods</span>
                <select className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1" value={periodType}
                  onChange={(e) => setPeriodType(e.target.value as MLayout['periodType'])}>
                  <option value="annual">Annual</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </label>
              <label className="block">
                <span className="text-gray-600">First year</span>
                <input type="number" className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1"
                  value={firstYear} onChange={(e) => setFirstYear(Number(e.target.value))} />
              </label>
              <label className="block">
                <span className="text-gray-600">Filed years</span>
                <input type="number" min={1} max={20} className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1"
                  value={filedYears} onChange={(e) => setFiledYears(Math.max(1, Number(e.target.value)))} />
              </label>
              <label className="block">
                <span className="text-gray-600">Forecast years</span>
                <select className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1" value={forecastYears}
                  onChange={(e) => setForecastYears(Number(e.target.value))}>
                  {Array.from({ length: MAX_FORECAST_YEARS }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
          )}
          {/* The source workbook, corrected, as a blank form - not IAS accounting's
              template, which is a different workbook for a different model. */}
          <Button size="sm" variant="quiet" className="mt-3" onClick={() => void downloadTemplate()}>
            Download template
          </Button>
        </Panel>

        <Panel
          title="Import a company's figures"
          description="A filing - uploaded or fetched from Companies House - or a workbook is read by the importers IAS accounting uses and placed in the baseline model's rows. A filled-in Baseline template or export is read cell for cell."
        >
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" disabled={busy} onClick={() => pdfInput.current?.click()}>
              Import accounts PDF
            </Button>
            <ReadAfresh checked={reread} onChange={setReread} disabled={busy} />
            <label className="flex items-center gap-1 text-xs text-gray-700">
              Forecast
              <select className="rounded border border-gray-300 px-1 py-0.5" value={forecastYears}
                onChange={(e) => setForecastYears(Number(e.target.value))} aria-label="Forecast years for an import">
                {Array.from({ length: MAX_FORECAST_YEARS }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n} yr</option>
                ))}
              </select>
            </label>
            <Button variant="secondary" disabled={busy} onClick={() => workbookInput.current?.click()}>
              Import workbook
            </Button>
            {importProgress && importKind === 'workbook' && (
              <span className="text-xs text-gray-600">{importProgress}</span>
            )}
          </div>
          <input
            ref={pdfInput}
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void importPdf(file, { reread, forecastYears });
              setReread(false);
            }}
          />
          <input
            ref={workbookInput}
            type="file"
            accept=".xlsx,.xlsm"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void importWorkbook(file);
            }}
          />
          {/* The same search and the same reading as IAS accounting - tagged figures where
              the filing has them - placed in the baseline model's rows. */}
          <CompaniesHouseImport onImport={importFromCompaniesHouse} busy={busy} />
        </Panel>
      </div>

      {/* IAS accounting's own panel - stages, a timer, what the importer is checking -
          because the reading behind it is the same one. */}
      {importProgress && importKind === 'pdf' && <PdfImportProgress message={importProgress} />}

      <FolderedProjects
        discipline={discipline.id}
        onCopied={() => void loadList()}
        isLoading={isLoading}
        empty="No projects yet. Start one from the template, or import a company's accounts."
        samples={samples.map((sample) => ({
          ...sample,
          onOpen: () =>
            void openSampleAt<{ project: { id: string } }>('/api/m', sample.key).then(async (body) => {
              await loadList();
              await open(body.project.id);
            }),
        }))}
        projects={list.map((row) => ({
          id: row.id,
          render: (folderControl, options) => (
          <ProjectRow
            key={row.id}
            folderControl={folderControl}
            title={row.name}
            meta={
              <>
                Version {row.version} · {row.inputCount} figure{row.inputCount === 1 ? '' : 's'} entered ·
                updated {new Date(row.updatedAt).toLocaleString()}
              </>
            }
            onOpen={() => void open(row.id)}
            onDelete={options?.sample ? undefined : () => void remove(row.id)}
          />
          ),
        }))}
      />
    </ProjectsPage>
  );
}
